import { Injectable } from "@angular/core";
import * as XLSX from "xlsx";

export interface ExcelHeader {
  key: string;
  label: string;
  type?: 'number' | 'currency' | 'text' | 'date';
  decimals?: number;
}

export interface TotalsConfig {
  columns: string[];
  calculations: { [key: string]: 'sum' | 'avg' | 'count' };
  useExcelFormulas?: boolean;
}

export interface ExcelExportConfig {
  data: any[];
  fileName: string;
  headers: ExcelHeader[];
  totalsConfig?: TotalsConfig;
  formatters?: { [key: string]: (value: any) => string };
  dateRange?: { start: Date; end: Date };
  sortBy?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  public exportToExcel(config: ExcelExportConfig): void {
    try {
      if (config.sortBy) {
        config.data = this.sortData(config.data, config.sortBy);
      }

      // Generate the sheet
      const ws: XLSX.WorkSheet = this.generateWorksheetWithFormulas(config);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Relatório");

      // Set the file name
      let finalFileName = config.fileName;
      if (config.dateRange) {
        const start = config.dateRange.start.toLocaleDateString('pt-BR').replace(/\//g, '-');
        const end = config.dateRange.end.toLocaleDateString('pt-BR').replace(/\//g, '-');
        finalFileName = `${config.fileName}_${start}_a_${end}`;
      }

      // Add a buffer to make the file safe for browsers
      const excelBuffer: any = XLSX.write(wb, { 
        bookType: "xlsx", 
        type: "array"
      });
      
      const blob: Blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" 
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${finalFileName}.xlsx`;
      a.click();

      //clear memory
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro na exportação Excel:", error);
    }
  }

  private generateWorksheetWithFormulas(config: ExcelExportConfig): XLSX.WorkSheet {
    const ws: XLSX.WorkSheet = {};
    const range = { s: { c: 0, r: 0 }, e: { c: config.headers.length - 1, r: config.data.length + 1 } };

    // Headers (row 0)
    config.headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: 0 });
      ws[cellAddress] = { v: header.label, t: 's' };
    });

    // Data rows (starting from row 1)
    config.data.forEach((item, rowIndex) => {
      const actualRowIndex = rowIndex + 1;
      
      config.headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: actualRowIndex });
        const rawValue = item[header.key];
        
        if (header.type === 'number') {
          const numValue = Number(rawValue) || 0;
          ws[cellAddress] = { v: numValue, t: 'n', z: this.numberFormat(header.decimals ?? 0) };
        } else if (header.type === 'currency') {
          const numValue = Number(rawValue) || 0;
          ws[cellAddress] = { v: numValue, t: 'n', z: this.numberFormat(header.decimals ?? 2) };
        } else if (header.type === 'date' && rawValue) {
          const brDate = ExcelExportService.toBrazilDate(rawValue);
          ws[cellAddress] = { v: brDate, t: 'd', z: 'dd/mm/yyyy hh:mm:ss' };
        } else {
          let cellValue = rawValue;
          if (config.formatters && config.formatters[header.key]) {
            cellValue = config.formatters[header.key](rawValue);
          }
          ws[cellAddress] = { v: cellValue || '', t: 's' };
        }
      });
    });

    if (config.totalsConfig) {
      const totalsRowIndex = config.data.length + 1;
      range.e.r = totalsRowIndex;

      config.headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: totalsRowIndex });

        if (config.totalsConfig?.columns.includes(header.key)) {
          if (config.totalsConfig.useExcelFormulas) {
            const calculation = config.totalsConfig.calculations[header.key];
            const formula = this.generateExcelFormula(
              calculation, 
              colIndex, 
              1,
              config.data.length
            );
            
            const totalDecimals = header.decimals ?? (header.type === 'currency' ? 2 : 0);
            ws[cellAddress] = { f: formula, t: 'n', z: this.numberFormat(totalDecimals) };
          } else {
            const calculation = config.totalsConfig.calculations[header.key];
            const totalDecimals = header.decimals ?? (header.type === 'currency' ? 2 : 0);
            const value = this.calculateTotal(config.data, header.key, calculation, totalDecimals);

            if (header.type === 'number' || header.type === 'currency') {
              ws[cellAddress] = { v: value, t: 'n', z: this.numberFormat(totalDecimals) };
            } else {
              const formattedValue = this.formatCellValue(value, header.key, config.formatters);
              ws[cellAddress] = { v: formattedValue, t: 's' };
            }
          }
        } else if (this.isNameColumn(header.key)) {
          ws[cellAddress] = { v: 'TOTAIS', t: 's' };
        } else {
          ws[cellAddress] = { v: '', t: 's' };
        }
      });
    }

    ws['!ref'] = XLSX.utils.encode_range(range);
    return ws;
  }

  private generateExcelFormula(
    calculation: 'sum' | 'avg' | 'count', 
    columnIndex: number, 
    startRow: number, 
    endRow: number
  ): string {
    const columnLetter = XLSX.utils.encode_col(columnIndex);
    const range = `${columnLetter}${startRow + 1}:${columnLetter}${endRow + 1}`;
    
    switch (calculation) {
      case 'sum':
        return `SUM(${range})`;
      case 'avg':
        return `AVERAGE(${range})`;
      case 'count':
        return `COUNT(${range})`;
      default:
        return `SUM(${range})`;
    }
  }

  private sortData(data: any[], sortBy: string): any[] {
    return [...data].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue, 'pt-BR');
      }
      
      return aValue > bValue ? 1 : -1;
    });
  }

  private formatCellValue(value: any, key: string, formatters?: { [key: string]: (value: any) => string }): any {
    if (formatters && formatters[key]) {
      return formatters[key](value);
    }

    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toLocaleDateString('pt-BR');
    
    return value;
  }

  private calculateTotal(data: any[], key: string, calculation: 'sum' | 'avg' | 'count', decimals: number = 2): number {
    switch (calculation) {
      case 'sum':
        return Number(
          data.reduce((acc, item) => acc + (Number(item[key]) || 0), 0).toFixed(decimals)
        );
      case 'avg':
        if (data.length === 0) return 0;
        const sum = data.reduce((acc, item) => acc + (Number(item[key]) || 0), 0);
        return Number((sum / data.length).toFixed(decimals));
      case 'count':
        return data.length;
      default:
        return 0;
    }
  }

  private numberFormat(decimals: number): string {
    return decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0';
  }

  private isNameColumn(key: string): boolean {
    const nameColumns = ['name', 'productName', 'description', 'titulo', 'nome'];
    return nameColumns.includes(key);
  }

  public static moneyFormatter = (value: any): string => {
    const numValue = Number(value) || 0;
    return numValue.toFixed(2).replace('.', ',');
  };

  public static dateFormatter = (value: any): string => {
    if (!value) return '';
    const date = ExcelExportService.toBrazilDate(value);
    return date.toLocaleDateString('pt-BR');
  };

  public static toBrazilDate = (value: any): Date => {
    const date = value instanceof Date ? value : new Date(value);
    return new Date(date.getTime() - 3 * 60 * 60 * 1000);
  };

  public static textFormatter = (value: any): string => {
    if (!value) return '';
    return value.toString();
  };

  public static integerFormatter = (value: any): string => {
    return (Number(value) || 0).toString();
  };

  public static percentageFormatter = (value: any): string => {
    const numValue = Number(value) || 0;
    return `${(numValue * 100).toFixed(2).replace('.', ',')}%`;
  };
}